import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '@careercard/convex/convex/_generated/api';
import type { Id } from '@careercard/convex/convex/_generated/dataModel';
import { CareerFocusWithMetrics } from '../hooks/focus/useCareerFocus';
import { useAuth } from './AuthContext';

interface FocusModeContextValue {
  isFocusMode: boolean;
  activeFocuses: CareerFocusWithMetrics[];
  currentFocus: CareerFocusWithMetrics | null;
  defaultFocus: CareerFocusWithMetrics | null;
  defaultFocusId: string | null;
  toggleFocusMode: () => void;
  setActiveFocuses: (focuses: CareerFocusWithMetrics[]) => void;
  setCurrentFocus: (focus: CareerFocusWithMetrics | null) => void;
  setDefaultFocus: (focusId: string | null) => void;
  setFocusMode: (enabled: boolean) => void;
}

const FocusModeContext = createContext<FocusModeContextValue | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useFocusMode = () => {
  const context = useContext(FocusModeContext);
  if (!context) {
    throw new Error('useFocusMode must be used within a FocusModeProvider');
  }
  return context;
};

interface FocusModeProviderProps {
  children: ReactNode;
}

export const FocusModeProvider: React.FC<FocusModeProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [isFocusMode, setIsFocusMode] = useState(false);
  const [activeFocuses, setActiveFocuses] = useState<CareerFocusWithMetrics[]>([]);
  const [currentFocus, setCurrentFocus] = useState<CareerFocusWithMetrics | null>(null);
  const [defaultFocusId, setDefaultFocusId] = useState<string | null>(null);

  // Convex mutations for persisting default focus
  const setDefaultMutation = useMutation(api.careerFocuses.setDefault);
  const clearDefaultMutation = useMutation(api.careerFocuses.update);

  // Query the default focus from Convex - only when authenticated
  const defaultFocusFromConvex = useQuery(
    api.careerFocuses.getDefault,
    isAuthenticated ? {} : "skip"
  );

  // Sync default focus ID from Convex when data is loaded
  useEffect(() => {
    if (defaultFocusFromConvex?._id) {
      setDefaultFocusId(defaultFocusFromConvex._id);
    }
  }, [defaultFocusFromConvex?._id]);

  // Load saved focus mode preference from localStorage
  useEffect(() => {
    if (user?.id) {
      const savedFocusMode = localStorage.getItem(`focusMode_${user.id}`);
      if (savedFocusMode !== null) {
        setIsFocusMode(savedFocusMode === 'true');
      }
    }
  }, [user?.id]);

  // Save focus mode preference when it changes
  useEffect(() => {
    if (user?.id) {
      localStorage.setItem(`focusMode_${user.id}`, String(isFocusMode));
    }
  }, [isFocusMode, user?.id]);

  const toggleFocusMode = () => {
    setIsFocusMode(prev => !prev);
  };

  const setFocusMode = (enabled: boolean) => {
    setIsFocusMode(enabled);
  };

  // Set or clear default focus - persists to Convex
  const setDefaultFocus = useCallback(async (focusId: string | null) => {
    try {
      if (focusId) {
        // Set this focus as default in Convex
        await setDefaultMutation({ id: focusId as Id<"userCareerFocuses"> });
        setDefaultFocusId(focusId);
      } else if (defaultFocusId) {
        // Clear the current default by setting isDefault to false
        await clearDefaultMutation({
          id: defaultFocusId as Id<"userCareerFocuses">,
          isDefault: false
        });
        setDefaultFocusId(null);
      }
    } catch (error) {
      console.error('Failed to update default focus:', error);
    }
  }, [defaultFocusId, setDefaultMutation, clearDefaultMutation]);

  // Calculate default focus from activeFocuses
  const defaultFocus = defaultFocusId ? activeFocuses.find(f => f.id === defaultFocusId) || null : null;

  const value: FocusModeContextValue = {
    isFocusMode,
    activeFocuses,
    currentFocus,
    defaultFocus,
    defaultFocusId,
    toggleFocusMode,
    setActiveFocuses,
    setCurrentFocus,
    setDefaultFocus,
    setFocusMode
  };

  return (
    <FocusModeContext.Provider value={value}>
      {children}
    </FocusModeContext.Provider>
  );
};